import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { AgrUser, CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { Customer } from '@next/core-lib/business-partner-aggregation-api';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

import { ViewMode } from 'src/app/shared/models/shared.model';

import {
  FecalScoring,
  FecalScoringDataSource,
  LocationItem,
} from '../../model/fecal-scoring.model';
import { OfflineService } from '../../services/offline.service';
import * as actions from '../../store/actions/fecal-scoring.actions';
import {
  getFecalScorings,
  getSelectedFecalScoring,
} from '../../store/selectors/fecal-scoring.selectors';

import { FecalScoringDetailComponent } from '../fecal-scoring-detail/fecal-scoring-detail.component';

@Component({
  selector: 'app-fecal-scoring-overview',
  templateUrl: './fecal-scoring-overview.component.html',
  styleUrls: ['./fecal-scoring-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    MatSelect,
    NgFor,
    MatOption,
    NgIf,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FecalScoringOverviewComponent implements OnDestroy {
  serviceWorkerRegistration: ServiceWorkerRegistration;
  pageTitle = this.localeService.translate('fecal-scoring.overview.title');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  upsertupsertFecalScoringOfflineObservable$: Observable<any>;
  upsertupsertFecalScoringOfflineObservableSubscription: Subscription;
  onlineEvent$: Observable<Event>;

  customer$: Observable<Customer> = this.store.select(UserSelectors.selectCustomer);

  isMobile$ = this.breakpointService.ltSm$;

  locations$: Observable<SelectedLocation[]> = this.store.select(UserSelectors.selectLocations);

  locationItems: LocationItem[];
  selectedLocations: LocationItem[];

  selectedLocation: number;
  locations: SelectedLocation[];
  customer: Customer;

  tableColumnClass: string;
  fecalScoringDatasource: FecalScoringDataSource[];

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };

  public tableConfig$: Observable<TableConfig> = this.store.select(getFecalScorings).pipe(
    skipWhile((fs) => !fs),
    map((fecalScorings: FecalScoring[]) => {
      let fecalScoringList: FecalScoring[] = [];
      if (fecalScorings) {
        fecalScoringList = fecalScorings;
      }

      if (!fecalScoringList) {
        return undefined;
      }

      const tableConfig: TableConfig = {
        ...this.tableConfigBase,
        columns: [
          {
            id: 'name',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('fecal-scoring.overview.table.fecalScoringName'),
          },
          {
            id: 'date',
            type: TableColumnTypes.DATE,
            label: this.localeService.translate('fecal-scoring.overview.table.fecalScoringDate'),
          },
          {
            id: 'createdBy',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('fecal-scoring.overview.table.fecalScoringAdvisor'),
            headerClassName: this.tableColumnClass,
            className: this.tableColumnClass,
          },
          {
            id: 'numberOfPens',
            type: TableColumnTypes.NUMBER,
            label: this.localeService.translate(
              'fecal-scoring.overview.table.fecalScoringNumberOfPens',
            ),
          },
          {
            id: 'ages',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('fecal-scoring.overview.table.fecalScoringAges'),
          },
          {
            id: 'location',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate(
              'fecal-scoring.overview.table.fecalScoringLocation',
            ),
          },
        ],
        actions: [
          {
            id: 'edit',
            callback: (row: FecalScoring) => this.openFecalScoringEdit(row.id),
            label: 'Edit',
            icon: 'edit',
            disabled: () => false,
            primary: false,
          },
          {
            id: 'delete',
            callback: (row: FecalScoring) => this.deleteFecalScoring(row),
            label: 'Delete',
            icon: 'delete',
            disabled: () => false,
            primary: false,
          },
        ],
      };

      if (fecalScorings) {
        this.createFecalScoringsDatasource(fecalScorings);

        tableConfig.dataSource = new MatTableDataSource(this.fecalScoringDatasource);
      } else {
        tableConfig.dataSource = new MatTableDataSource([]);
      }
      return tableConfig;
    }),
  );

  constructor(
    @Inject(BreakpointServiceInjector)
    private readonly breakpointService: BreakpointService,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly dialog: DialogService,
    private readonly offlineService: OfflineService,
  ) {
    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      this.tableColumnClass = isMobile ? 'mobile' : '';
    });

    this.locations$
      .pipe(
        skipWhile((l) => !l),
        take(2),
      )
      .subscribe((locations) => {
        this.locations = locations;
        this.locationItems = locations.map(
          (userlocation) =>
            <LocationItem>{
              value: userlocation.id,
              name: userlocation.name,
              selected: userlocation.isSelected,
            },
        );
        this.selectedLocations = this.locationItems;
      });

    this.customer$
      .pipe(
        take(2),
        skipWhile((customer) => customer === undefined || customer === null),
      )
      .subscribe((customer) => {
        this.customer = customer;
        this.upsertupsertFecalScoringOfflineObservable$ =
          this.offlineService.getOfflineDataObservable$(customer.id, 'UpsertFecalScoring');
      });

    this.upsertupsertFecalScoringOfflineObservableSubscription =
      this.upsertupsertFecalScoringOfflineObservable$?.subscribe((a) => {
        if (window.navigator.onLine && a?.type === 'RETRIEVE_OFFLINE_DATA') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const requests: FecalScoring[] = a?.payload?.map((payload: any) => payload?.payload);

          let lastrequest: FecalScoring = null;

          requests.forEach((request: FecalScoring) => {
            if (lastrequest && request.id === undefined && request.name === lastrequest.name) {
              this.store
                .select(getSelectedFecalScoring)
                .pipe(
                  skipWhile((c) => !c),
                  take(1),
                )
                .subscribe((fecalScoring) => {
                  request.id = fecalScoring.id;

                  this.store.dispatch(
                    actions.upsertFecalScoringRequest({
                      request,
                    }),
                  );
                });
            } else {
              this.store.dispatch(
                actions.upsertFecalScoringRequest({
                  request,
                }),
              );
            }

            lastrequest = request;
          });

          this.clearCachedData();

          this.store.dispatch(actions.clearFecalScoringState());
          this.store.dispatch(actions.getFecalScoringsRequest());
        }
      });

    this.onlineEvent$ = fromEvent(window, 'online');
    this.onlineEvent$.subscribe(() => {
      // handle online mode
      this.getCachedData();
    });
  }

  ngOnDestroy(): void {
    if (this.upsertupsertFecalScoringOfflineObservableSubscription) {
      this.upsertupsertFecalScoringOfflineObservableSubscription.unsubscribe();
    }
  }

  getCachedData() {
    this.customer$
      .pipe(
        take(1),
        skipWhile((customer) => customer === undefined || customer === null),
      )
      .subscribe((customer) => {
        this.offlineService.dispatchGetOfflineData(customer.id, 'UpsertFecalScoring');
      });
  }

  clearCachedData() {
    this.customer$
      .pipe(
        take(1),
        skipWhile((customer) => customer === undefined || customer === null),
      )
      .subscribe((customer) => {
        this.offlineService.dispatchDeleteOfflineData(customer.id, 'UpsertFecalScoring');
      });
  }

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  createFecalScoring() {
    this.store.dispatch(actions.clearSelectedFecalScoring());
    this.store
      .select(UserSelectors.selectUser)
      .pipe(take(1))
      .subscribe((user: AgrUser) => {
        this.dialog
          .select({
            options: this.locationItems,
            selected: null,
            emptyText: 'No options available',
            pushHistoryState: false,
            title: this.localeService.translate('fecal-scoring.detail.create.chooselocation.title'),
          })
          .subscribe(({ status, result }) => {
            if (status === DIALOG_RETURN_TYPES.confirm) {
              this.selectedLocation = result;
              this.dialog
                .fullscreenDialog(
                  FecalScoringDetailComponent,
                  {
                    confirmButton: true,
                    confirmButtonText: this.localeService.translate('save'),
                    altButton: true,
                    altButtonText: this.localeService.translate(
                      'fecal-scoring.detail.save-and-create-new',
                    ),
                    altButtonColor: 'primary',
                    altButtonIcon: 'save',
                    setPendingChanges: false,
                    fecalScoringId: null,
                    locationId: this.selectedLocation,
                    locations: this.locations,
                    customer: this.customer,
                    viewMode: ViewMode.CREATE,
                  },
                  {
                    maxWidth: '100vw',
                  },
                )
                .subscribe((result) => {
                  if (result.status === DIALOG_RETURN_TYPES.confirm) {
                    const request = result.result.fecalScoring;
                    request.createdBy = user.name;
                    request.createdAt = new Date().toISOString();
                    request.date = new Date().toISOString();
                    this.store.dispatch(actions.upsertFecalScoringRequest({ request }));
                  }
                });
            }
          });
      });
  }

  openFecalScoringEdit(fecalScoringId: number) {
    this.store.dispatch(
      actions.setSelectedFecalScoringRequest({
        id: fecalScoringId,
      }),
    );
    this.dialog
      .fullscreenDialog(
        FecalScoringDetailComponent,
        {
          confirmButton: true,
          confirmButtonText: this.localeService.translate('save'),
          altButton: true,
          altButtonText: this.localeService.translate('fecal-scoring.detail.save-and-create-new'),
          altButtonColor: 'primary',
          altButtonIcon: 'save',
          setPendingChanges: false,
          fecalScoringId,
          locations: this.locations,
          customer: this.customer,
          viewMode: ViewMode.EDIT,
        },
        {
          maxWidth: '100vw',
        },
      )
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(
            actions.upsertFecalScoringRequest({ request: result.result.fecalScoring }),
          );
          this.store.dispatch(actions.loadData());
        }
      });
  }

  deleteFecalScoring(fecalScoring: FecalScoring) {
    this.store.dispatch(actions.deleteFecalScoringWithConfirmation({ fecalScoring }));
  }

  // helper function to set selected Locations in the multiselect in the header.
  // We need to use a compareWith function because it does not work automatically in this case.
  areLocationsEqual(li1: LocationItem, li2: LocationItem): boolean {
    if (!li1 || !li2) {
      return false;
    }
    return li1.name === li2.name && li1.value === li2.value && li1.selected === li2.selected;
  }

  changeLocation(selection: MatSelectChange) {
    const selected = (<LocationItem[]>selection.value).map((val) => val.value);
    const feedAdviceDataSource = this.fecalScoringDatasource?.filter((x) =>
      selected.some((id) => x.locationId === id),
    );

    if (feedAdviceDataSource) {
      this.tableConfig$ = this.tableConfig$.pipe(
        map((tableConfig) => ({
          ...tableConfig,
          dataSource: new MatTableDataSource(feedAdviceDataSource),
        })),
      );
    }
  }

  private createFecalScoringsDatasource(fecalScorings: FecalScoring[]) {
    this.fecalScoringDatasource = fecalScorings.map(
      (fecalScoring: FecalScoring) =>
        ({
          id: fecalScoring.id,
          name: fecalScoring.name,
          date: fecalScoring.date,
          createdBy: fecalScoring.createdBy,
          numberOfPens: fecalScoring.pens.length,

          ages: fecalScoring.pens
            .map((pen) => pen.age)
            .filter((x, i, a) => x && a.indexOf(x) === i)
            .sort(function (x, y) {
              return x - y;
            })
            .toString()
            .replace(/,/g, ', '),
          location: this.locations.find((l) => l.id === fecalScoring.locationId)?.name,
          locationId: this.locations.find((l) => l.id === fecalScoring.locationId)?.id,
        }) as FecalScoringDataSource,
    );
  }
}
